/**
 * Section-type registry (Phase B Step 6 — Structured Editor v2).
 *
 * Maps `{page_slug}:{section_id}` → schema { title, fields: [...] }.
 *
 * Field shape:
 *   { key, type, label, help?, options?, itemLabel?, fields? }
 *
 * type is one of:
 *   text        single-line input
 *   textarea    multi-line input
 *   number      numeric input
 *   boolean     switch/checkbox
 *   url         external URL (validated as-is via a Media picker later)
 *   href        internal path / anchor / external URL — free text
 *   asset_key   pairs with a "Pick from library" button (media picker)
 *   select      constrained values (options: [{value, label}])
 *   array       repeating group; `fields` is the sub-schema, `itemLabel` builds each row title
 *   object      nested group; `fields` is the sub-schema
 *
 * Sections without a registered schema fall back to the raw-JSON editor.
 */

// ── Common option lists ──
const ICON_OPTIONS = [
  'GraduationCap', 'Globe', 'Award', 'Users', 'Trophy', 'Star', 'Plane',
  'Building', 'BarChart3', 'Target', 'BookOpen', 'Sparkles', 'Compass',
  'Briefcase', 'Wallet', 'TrendingUp', 'CheckCircle2', 'PiggyBank',
].map((v) => ({ value: v, label: v }));

const SOCIAL_PLATFORMS = [
  'facebook', 'instagram', 'linkedin', 'youtube', 'twitter', 'threads', 'tiktok',
].map((v) => ({ value: v, label: v }));

const CTA_VARIANTS = ['primary', 'secondary', 'ghost', 'outline'].map((v) => ({ value: v, label: v }));

// ── Reusable field groups ──
const HEADING_FIELDS = [
  { key: 'text', type: 'text', label: 'Heading text' },
  { key: 'level', type: 'number', label: 'Heading level (1-6)', help: 'H1, H2, …' },
];

const SUBHEADING_FIELDS = [
  { key: 'text', type: 'textarea', label: 'Sub-heading text' },
];

const CTA_FIELDS = [
  { key: 'label', type: 'text', label: 'Button label' },
  { key: 'href', type: 'href', label: 'Link URL' },
  { key: 'variant', type: 'select', label: 'Style variant', options: CTA_VARIANTS },
];

const SECTION_HEADER_FIELDS = [
  { key: 'eyebrow', type: 'text', label: 'Eyebrow / small label above heading' },
  { key: 'heading', type: 'text', label: 'Heading' },
  { key: 'sub_heading', type: 'textarea', label: 'Sub-heading' },
];

// ── Section schema registry ──
export const SECTION_SCHEMAS = {
  // -------------------- HOMEPAGE --------------------
  'homepage:hero_badge': {
    title: 'Hero badge',
    fields: [
      { key: 'text', type: 'text', label: 'Badge text', help: 'Small pill shown above the hero headline.' },
    ],
  },

  'homepage:hero': {
    title: 'Hero',
    fields: [
      { key: 'heading', type: 'object', label: 'Heading', fields: HEADING_FIELDS },
      { key: 'sub_heading', type: 'object', label: 'Sub-heading', fields: SUBHEADING_FIELDS },
      { key: 'cta_primary', type: 'object', label: 'Primary CTA', fields: CTA_FIELDS },
      { key: 'cta_secondary', type: 'object', label: 'Secondary CTA', fields: CTA_FIELDS },
    ],
  },

  'homepage:hero_visual': {
    title: 'Hero visual & glass stat cards',
    fields: [
      { key: 'background_asset_key', type: 'asset_key', label: 'Background image (asset key)' },
      { key: 'background_alt', type: 'text', label: 'Background alt text (a11y)' },
      {
        key: 'hero_stat_cards',
        type: 'array',
        label: 'Glass stat cards (max 3)',
        itemLabel: (item, i) => item?.label || item?.value || `Card ${i + 1}`,
        fields: [
          { key: 'icon', type: 'select', label: 'Icon', options: ICON_OPTIONS },
          { key: 'value', type: 'text', label: 'Big value', help: 'e.g. "30,000+"' },
          { key: 'label', type: 'text', label: 'Label' },
          { key: 'sublabel', type: 'text', label: 'Sub-label (optional)' },
        ],
      },
    ],
  },

  'homepage:stats': {
    title: 'Stats strip',
    fields: [
      {
        key: 'items',
        type: 'array',
        label: 'Stat items',
        itemLabel: (i, idx) => i?.label || `Stat ${idx + 1}`,
        fields: [
          { key: 'icon', type: 'select', label: 'Icon', options: ICON_OPTIONS },
          { key: 'value', type: 'text', label: 'Value' },
          { key: 'label', type: 'text', label: 'Label' },
        ],
      },
    ],
  },

  'homepage:section_headers': {
    title: 'Section headers (destinations / testimonials / journey)',
    fields: [
      { key: 'destinations', type: 'object', label: 'Destinations header', fields: SECTION_HEADER_FIELDS },
      { key: 'testimonials', type: 'object', label: 'Testimonials header', fields: SECTION_HEADER_FIELDS },
      { key: 'journey', type: 'object', label: 'Journey header', fields: SECTION_HEADER_FIELDS },
    ],
  },

  'homepage:video_promo': {
    title: 'Video promo',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Eyebrow' },
      { key: 'heading', type: 'object', label: 'Heading', fields: HEADING_FIELDS },
      { key: 'video_url', type: 'url', label: 'Video URL (YouTube / Vimeo / MP4)' },
      { key: 'poster_asset_key', type: 'asset_key', label: 'Poster image (asset key)' },
      { key: 'poster_alt', type: 'text', label: 'Poster alt text' },
      { key: 'duration_seconds', type: 'number', label: 'Duration (seconds)' },
    ],
  },

  'homepage:university_marquee': {
    title: 'University marquee',
    fields: [
      { key: 'eyebrow', type: 'text', label: 'Eyebrow' },
      {
        key: 'universities',
        type: 'array',
        label: 'Universities',
        itemLabel: (u, i) => u?.name || `University ${i + 1}`,
        fields: [
          { key: 'name', type: 'text', label: 'Name' },
          { key: 'logo_asset_key', type: 'asset_key', label: 'Logo (asset key)' },
        ],
      },
    ],
  },

  'homepage:testimonials': {
    title: 'Testimonials',
    fields: [
      {
        key: 'items',
        type: 'array',
        label: 'Testimonials',
        itemLabel: (t, i) => t?.name || `Testimonial ${i + 1}`,
        fields: [
          { key: 'name', type: 'text', label: 'Student name' },
          { key: 'university', type: 'text', label: 'University' },
          { key: 'program', type: 'text', label: 'Program' },
          { key: 'quote', type: 'textarea', label: 'Quote' },
          { key: 'photo_asset_key', type: 'asset_key', label: 'Photo (asset key)' },
        ],
      },
    ],
  },

  'homepage:journey_settings': {
    title: 'Journey selector personas',
    fields: [
      {
        key: 'personas',
        type: 'array',
        label: 'Personas',
        itemLabel: (p, i) => p?.title || `Persona ${i + 1}`,
        fields: [
          { key: 'id', type: 'text', label: 'Stable id (slug)' },
          { key: 'title', type: 'text', label: 'Title' },
          { key: 'description', type: 'textarea', label: 'Description' },
          { key: 'icon', type: 'select', label: 'Icon', options: ICON_OPTIONS },
          { key: 'stats_label', type: 'text', label: 'Stat label' },
          { key: 'stats_value', type: 'text', label: 'Stat value' },
          { key: 'cta', type: 'text', label: 'CTA text' },
        ],
      },
    ],
  },

  'homepage:final_cta': {
    title: 'Final CTA strip',
    fields: [
      { key: 'heading', type: 'object', label: 'Heading', fields: HEADING_FIELDS },
      { key: 'sub_heading', type: 'object', label: 'Sub-heading', fields: SUBHEADING_FIELDS },
      { key: 'cta_primary', type: 'object', label: 'Primary CTA', fields: CTA_FIELDS },
      { key: 'cta_secondary', type: 'object', label: 'Secondary CTA', fields: CTA_FIELDS },
    ],
  },

  // -------------------- HEADER --------------------
  'header:top_cta': {
    title: 'Header CTA button',
    fields: [
      { key: 'button', type: 'object', label: 'Desktop button', fields: CTA_FIELDS },
      {
        key: 'mobile_button', type: 'object', label: 'Mobile button (optional override)',
        fields: [
          { key: 'label', type: 'text', label: 'Label' },
          { key: 'href', type: 'href', label: 'URL' },
        ],
      },
    ],
  },

  'header:brand': {
    title: 'Header brand',
    fields: [
      { key: 'logo_asset_key', type: 'asset_key', label: 'Logo (asset key)' },
      { key: 'logo_alt', type: 'text', label: 'Logo alt text' },
      { key: 'wordmark', type: 'text', label: 'Wordmark (fallback text)' },
    ],
  },

  'header:nav_links': {
    title: 'Top-level navigation links',
    fields: [
      {
        key: 'items',
        type: 'array',
        label: 'Nav links',
        itemLabel: (l, i) => l?.label || `Link ${i + 1}`,
        fields: [
          { key: 'label', type: 'text', label: 'Label' },
          { key: 'href', type: 'href', label: 'URL' },
          { key: 'is_active', type: 'boolean', label: 'Active (visible in nav)' },
          { key: 'mega_menu', type: 'text', label: 'Mega-menu key (optional)', help: 'e.g. destinations, programs, services' },
        ],
      },
    ],
  },

  'header:destinations_menu': {
    title: 'Destinations mega-menu',
    fields: [
      {
        key: 'columns',
        type: 'array',
        label: 'Columns',
        itemLabel: (c, i) => c?.heading || `Column ${i + 1}`,
        fields: [
          { key: 'heading', type: 'text', label: 'Column heading' },
          {
            key: 'links',
            type: 'array',
            label: 'Links',
            itemLabel: (l, i) => l?.label || `Link ${i + 1}`,
            fields: [
              { key: 'label', type: 'text', label: 'Label' },
              { key: 'href', type: 'href', label: 'URL' },
            ],
          },
        ],
      },
    ],
  },

  // -------------------- FOOTER --------------------
  'footer:brand': {
    title: 'Footer brand',
    fields: [
      { key: 'company_name', type: 'text', label: 'Company name' },
      { key: 'tagline', type: 'text', label: 'Tagline' },
      { key: 'logo_asset_key', type: 'asset_key', label: 'Logo (asset key)' },
      { key: 'logo_alt', type: 'text', label: 'Logo alt text' },
    ],
  },

  'footer:contact': {
    title: 'Footer contact',
    fields: [
      { key: 'phone', type: 'text', label: 'Phone (display)' },
      { key: 'phone_href', type: 'href', label: 'Phone href (tel:…)' },
      { key: 'email', type: 'text', label: 'Email (display)' },
      { key: 'email_href', type: 'href', label: 'Email href (mailto:…)' },
      { key: 'whatsapp_number', type: 'text', label: 'WhatsApp number (display)' },
      { key: 'whatsapp_href', type: 'href', label: 'WhatsApp href (https://wa.me/…)' },
      { key: 'address', type: 'textarea', label: 'Address / office info' },
      { key: 'hours', type: 'text', label: 'Working hours' },
    ],
  },

  'footer:social': {
    title: 'Footer social links',
    fields: [
      {
        key: 'items',
        type: 'array',
        label: 'Social channels',
        itemLabel: (s, i) => s?.platform || `Channel ${i + 1}`,
        fields: [
          { key: 'platform', type: 'select', label: 'Platform', options: SOCIAL_PLATFORMS },
          { key: 'href', type: 'url', label: 'URL' },
          { key: 'is_active', type: 'boolean', label: 'Active' },
        ],
      },
    ],
  },

  'footer:legal': {
    title: 'Footer legal',
    fields: [
      { key: 'copyright', type: 'text', label: 'Copyright line' },
      {
        key: 'links',
        type: 'array',
        label: 'Legal links',
        itemLabel: (l, i) => l?.label || `Link ${i + 1}`,
        fields: [
          { key: 'label', type: 'text', label: 'Label' },
          { key: 'href', type: 'href', label: 'URL' },
        ],
      },
    ],
  },
};

/** Lookup helper. Returns undefined for sections without a registered schema. */
export const getSectionSchema = (pageSlug, sectionId) =>
  SECTION_SCHEMAS[`${pageSlug}:${sectionId}`];

/** Build a blank value for a field, used when adding a new array item. */
export const buildBlankValue = (field) => {
  switch (field.type) {
    case 'boolean': return false;
    case 'number': return 0;
    case 'array': return [];
    case 'object': {
      const obj = {};
      for (const f of field.fields || []) obj[f.key] = buildBlankValue(f);
      return obj;
    }
    default: return '';
  }
};

export default SECTION_SCHEMAS;
